import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { BarChart, ChartFrame, DoughnutChart, Sparkline } from "../../components/charts";
import {
  AMD_COLOR,
  SOFIA_STATES,
  STATE_COLOR,
  sliceColor,
  sofiaStateBucket,
  trunkChartLabel,
  trunkColor,
} from "../../components/chartTheme";
import { Card, EmptyState, PageBody, PageHeader, StatusPill, cx } from "../../components/ui";
import { ApiError, request } from "../../lib/api";
import {
  ANATOMY_KEYS,
  EMPTY_ANATOMY,
  EMPTY_SLICES,
  EMPTY_VOICE,
  SLICE_KEYS,
  VOICE_KEYS,
  type AgentRow,
  type AgentsPayload,
  type AmdQualityPayload,
  type AsrMode,
  type BestPathPayload,
  type CatalogPayload,
  type CatalogRow,
  type ClassificationPayload,
  type DayPartPayload,
  type FailureAnatomy,
  type FailuresPayload,
  type HangupSlices,
  type JudgmentsPayload,
  type MatrixCell,
  type MatrixPayload,
  type Measures,
  type MixCounts,
  type OtpFunnel,
  type OtpFunnelPayload,
  type Overview,
  type PageRow,
  type PagesPayload,
  type TopCell,
  type TrunkHealth,
  type VoiceMix,
} from "./types";

type TabId =
  | "overview"
  | "matrix"
  | "failures"
  | "otp"
  | "judgments"
  | "day-part"
  | "best-path"
  | "classification"
  | "amd-quality"
  | "trunks"
  | "catalog"
  | "agents"
  | "pages";

const PRIMARY_TABS: Array<{ id: TabId; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "matrix", label: "Trunk × country" },
  { id: "failures", label: "Failures" },
  { id: "otp", label: "OTP funnel" },
  { id: "judgments", label: "Outcomes" },
  { id: "day-part", label: "Day part" },
];

const MORE_TABS: Array<{ id: TabId; label: string }> = [
  { id: "classification", label: "Far-end class" },
  { id: "amd-quality", label: "AMD quality" },
  { id: "trunks", label: "Trunk health" },
  { id: "catalog", label: "Dial catalog" },
  { id: "best-path", label: "Shadow ranks" },
  { id: "agents", label: "Agents" },
  { id: "pages", label: "Pages" },
];

const VOICE_COLOR: Record<keyof VoiceMix, string> = {
  human: AMD_COLOR.HUMAN,
  screening: AMD_COLOR.SCREENING,
  machine: AMD_COLOR.MACHINE,
  other: AMD_COLOR.UNKNOWN,
};

const SLICE_COLOR: Record<keyof HangupSlices, string> = {
  sip: "#7c8cf8",
  no_media: "#e57584",
  dest: "#d8a25c",
  policy: "#95a3ff",
  other: "#6f7782",
};

const SLICE_LABEL: Record<keyof HangupSlices, string> = {
  sip: "SIP",
  no_media: "No media",
  dest: "Dest",
  policy: "Policy",
  other: "Other",
};

const FORBIDDEN_KEY =
  /^(password|passwd|secret|token|digits|otp_code|expected_code|pin|cli_secret)$/i;
const DIGIT_KEY = /^\d{4,}$/;

function isForbiddenKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (FORBIDDEN_KEY.test(key) || DIGIT_KEY.test(key)) return true;
  return lower.includes("password") || lower.includes("secret");
}

function pct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) return "—";
  const n = Number(rate);
  const value = n <= 1 && n >= -1 ? n * 100 : n;
  return `${value.toFixed(1)}%`;
}

function num(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return String(value);
}

function isThin(sampleThin: boolean | undefined, n: number | undefined, nMin: number | undefined): boolean {
  const min = nMin ?? 30;
  if (sampleThin) return true;
  if (typeof n === "number" && n < min) return true;
  return false;
}

function mixRows(mix: MixCounts | null | undefined): Array<[string, number]> {
  return Object.entries(mix ?? {})
    .filter(([key]) => !isForbiddenKey(key))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

function chartMix(mix: MixCounts | null | undefined, max?: number): Array<[string, number]> {
  const rows = mixRows(mix).filter(([key]) => !/^\d{3,}$/.test(key.trim()));
  return typeof max === "number" ? rows.slice(0, max) : rows;
}

function mixSum(rows: Array<[string, number]>): number {
  return rows.reduce((sum, [, n]) => sum + (Number.isFinite(n) ? n : 0), 0);
}

function trunkAttemptBars(cells: MatrixCell[] | undefined): Array<{ id: string; n: number }> {
  const map = new Map<string, number>();
  for (const cell of cells ?? []) {
    const id = (cell.trunk_id || "").trim();
    if (!id) continue;
    map.set(id, (map.get(id) ?? 0) + (Number(cell.attempts) || 0));
  }
  return [...map.entries()]
    .map(([id, n]) => ({ id, n }))
    .filter((row) => row.n > 0)
    .sort((a, b) => b.n - a.n || a.id.localeCompare(b.id))
    .slice(0, 12);
}

function PaneChart({
  title,
  hint,
  ariaLabel,
  emptyTitle,
  emptyBody,
  ready,
  height = "h-[220px]",
  children,
}: {
  title: string;
  hint?: string;
  ariaLabel: string;
  emptyTitle: string;
  emptyBody: string;
  ready: boolean;
  height?: string;
  children: ReactNode;
}) {
  if (!ready) return <EmptyState title={emptyTitle} body={emptyBody} />;
  return (
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">{title}</div>
      {hint ? <p className="mb-3 text-xs text-mute">{hint}</p> : null}
      <ChartFrame ariaLabel={ariaLabel} className={height}>
        {children}
      </ChartFrame>
    </Card>
  );
}

function anatomyOf(value: FailureAnatomy | null | undefined): FailureAnatomy {
  return {
    trunk: Number(value?.trunk ?? 0) || 0,
    dest: Number(value?.dest ?? 0) || 0,
    quality: Number(value?.quality ?? 0) || 0,
    policy: Number(value?.policy ?? 0) || 0,
  };
}

function voiceOf(value: VoiceMix | null | undefined): VoiceMix {
  return {
    human: Number(value?.human ?? 0) || 0,
    screening: Number(value?.screening ?? 0) || 0,
    machine: Number(value?.machine ?? 0) || 0,
    other: Number(value?.other ?? 0) || 0,
  };
}

function slicesOf(value: HangupSlices | null | undefined): HangupSlices {
  return {
    sip: Number(value?.sip ?? 0) || 0,
    no_media: Number(value?.no_media ?? 0) || 0,
    dest: Number(value?.dest ?? 0) || 0,
    policy: Number(value?.policy ?? 0) || 0,
    other: Number(value?.other ?? 0) || 0,
  };
}

function voiceSum(mix: VoiceMix): number {
  return VOICE_KEYS.reduce((sum, key) => sum + mix[key], 0);
}

function sliceSum(mix: HangupSlices): number {
  return SLICE_KEYS.reduce((sum, key) => sum + mix[key], 0);
}

function statusTone(status: string | null | undefined): "ok" | "live" | "fail" | "mute" {
  const raw = (status ?? "").toLowerCase();
  if (raw === "live" || raw === "reged" || raw === "up" || raw === "ok" || raw === "success") return "ok";
  if (raw === "shadow" || raw === "noreg" || raw === "partial") return "live";
  if (
    raw === "fail" ||
    raw === "failed" ||
    raw === "down" ||
    raw.includes("fail") ||
    raw === "void"
  ) {
    return "fail";
  }
  return "mute";
}

function asrOf(row: Pick<Measures, "human_asr" | "network_asr">, mode: AsrMode): number | null {
  return mode === "human" ? row.human_asr : row.network_asr;
}

async function requestOptional<T>(path: string, signal: AbortSignal): Promise<T | null> {
  try {
    return await request<T>(path, { signal });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 405 || err.status === 501)) {
      return null;
    }
    throw err;
  }
}

function parseTrunks(raw: unknown): TrunkHealth[] {
  if (raw == null) return [];
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "object") {
    const rec = raw as Record<string, unknown>;
    if (Array.isArray(rec.trunks)) list = rec.trunks;
    else if (Array.isArray(rec.items)) list = rec.items;
    else if (Array.isArray(rec.gateways)) list = rec.gateways;
    else if (Array.isArray(rec.health)) list = rec.health;
  }
  const out: TrunkHealth[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const id = String(rec.trunk_id ?? rec.id ?? rec.name ?? "").trim();
    if (!id) continue;
    const displayRaw = rec.display == null ? "" : String(rec.display).trim();
    const stateRaw = rec.sofia_state ?? rec.state;
    const state = stateRaw == null ? null : String(stateRaw);
    const status = rec.active ? "active" : rec.register === false ? "peer" : "register";
    out.push({ id, state, status, display: displayRaw || null });
  }
  return out;
}

function LowConfidence({
  sampleThin,
  n,
  nMin,
}: {
  sampleThin?: boolean;
  n?: number;
  nMin?: number;
}) {
  const min = nMin ?? 30;
  if (!isThin(sampleThin, n, min)) return null;
  return (
    <div
      className="mb-4 rounded-[var(--radius-card)] border border-live/40 bg-live/10 px-4 py-3 text-sm text-ink"
      role="status"
    >
      {`low confidence (n<${min}) — do not treat ranks as proven`}
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card className="px-5 py-4">
      <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">{label}</div>
      <div className="mt-1 text-2xl text-ink tabular-nums">{value}</div>
      {hint ? <div className="mt-1 text-xs text-mute">{hint}</div> : null}
    </Card>
  );
}

function MixTable({
  title,
  mix,
  empty,
}: {
  title: string;
  mix: MixCounts | null | undefined;
  empty: string;
}) {
  const rows = mixRows(mix);
  return (
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">{title}</div>
      {rows.length === 0 ? (
        <EmptyState title="Empty" body={empty} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
              <tr>
                <th className="py-2 pr-3">Key</th>
                <th className="py-2 pr-3">n</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([key, value]) => (
                <tr key={key} className="border-t border-white/5">
                  <td className="py-2 pr-3 font-mono text-xs">{key}</td>
                  <td className="py-2 pr-3 tabular-nums">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AnatomyTable({ anatomy }: { anatomy: FailureAnatomy }) {
  const body = anatomyOf(anatomy);
  return (
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
        Failure anatomy
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
            <tr>
              <th className="py-2 pr-3">Bucket</th>
              <th className="py-2 pr-3">n</th>
            </tr>
          </thead>
          <tbody>
            {ANATOMY_KEYS.map((key) => (
              <tr key={key} className="border-t border-white/5">
                <td className="py-2 pr-3 font-mono text-xs">{key}</td>
                <td className="py-2 pr-3 tabular-nums">{body[key]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ShadowBanner({ extra }: { extra?: string }) {
  return (
    <div
      className="mb-4 rounded-[var(--radius-card)] border border-live/50 bg-live/10 px-4 py-3 text-sm text-ink"
      role="note"
    >
      <StatusPill tone="live">SHADOW</StatusPill>
      <span className="ml-2">shadow, not Sofia — does not pick the Dial trunk</span>
      {extra ? <span className="ml-2 text-mute">{extra}</span> : null}
    </div>
  );
}

function TabButtons({
  items,
  tab,
  onChange,
}: {
  items: Array<{ id: TabId; label: string }>;
  tab: TabId;
  onChange: (next: TabId) => void;
}) {
  return (
    <>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={tab === item.id}
          className={cx(
            "rounded-[var(--radius-control)] px-3 py-1.5 text-sm font-medium",
            tab === item.id ? "border border-border bg-surface text-ink" : "text-mute hover:text-ink",
          )}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </>
  );
}

function TabNav({ tab, onChange }: { tab: TabId; onChange: (next: TabId) => void }) {
  return (
    <div className="mb-6 space-y-2">
      <nav className="flex flex-wrap gap-1" role="tablist" aria-label="Voice ops">
        <TabButtons items={PRIMARY_TABS} tab={tab} onChange={onChange} />
      </nav>
      <nav className="flex flex-wrap items-center gap-1" role="tablist" aria-label="More voice ops">
        <span className="px-1 text-[11px] uppercase tracking-[0.14em] text-mute">More</span>
        <TabButtons items={MORE_TABS} tab={tab} onChange={onChange} />
      </nav>
    </div>
  );
}

function AsrToggle({ value, onChange }: { value: AsrMode; onChange: (next: AsrMode) => void }) {
  return (
    <div
      className="mb-4 inline-flex rounded-[var(--radius-control)] p-0.5 hairline"
      role="tablist"
      aria-label="ASR mode"
    >
      {(
        [
          { id: "human", label: "Human ASR" },
          { id: "network", label: "Network ASR" },
        ] as const
      ).map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={value === item.id}
          className={cx(
            "rounded-[calc(var(--radius-control)-2px)] px-3 py-1.5 text-sm font-medium",
            value === item.id ? "bg-surface text-ink" : "text-mute hover:text-ink",
          )}
          onClick={() => onChange(item.id)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function MeasuresGrid({
  title,
  row,
  mode,
}: {
  title?: string;
  row: Measures;
  mode: AsrMode;
}) {
  return (
    <div className="space-y-3">
      {title ? (
        <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">{title}</div>
      ) : null}
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Attempts" value={num(row.attempts)} />
        <StatCard
          label={mode === "human" ? "Human ASR" : "Network ASR"}
          value={pct(asrOf(row, mode))}
          hint={mode === "human" ? "HUMAN only — VM not folded in" : "network connect classes"}
        />
        <StatCard label="Quality fail" value={pct(row.quality_fail_rate)} />
        <StatCard label="Rotate rate" value={pct(row.rotate_rate)} />
        <StatCard label="Human n" value={num(row.human_n)} hint="far_end HUMAN" />
        <StatCard label="Network n" value={num(row.network_n)} hint="connect classes" />
        <StatCard label="Quality n" value={num(row.quality_n)} />
        <StatCard label="n min" value={num(row.n_min ?? 30)} />
      </div>
    </div>
  );
}

function VoiceMixChart({ mix, title }: { mix: VoiceMix; title: string }) {
  const ready = voiceSum(mix) > 0;
  return (
    <PaneChart
      title={title}
      ariaLabel="Human, screening, and machine counts"
      emptyTitle="No voice mix"
      emptyBody="no call records yet"
      ready={ready}
    >
      <DoughnutChart
        slices={VOICE_KEYS.filter((key) => mix[key] > 0).map((key) => ({
          label: key,
          value: mix[key],
          color: VOICE_COLOR[key],
        }))}
      />
    </PaneChart>
  );
}

function SliceChart({ slices, title }: { slices: HangupSlices; title: string }) {
  const ready = sliceSum(slices) > 0;
  return (
    <PaneChart
      title={title}
      hint="SIP cause vs no-media vs destination. Other is clearing / VM / operator — not a setup fail."
      ariaLabel="Hangup slices: SIP, no media, destination, policy, other"
      emptyTitle="No hangup slices"
      emptyBody="no hangup causes yet"
      ready={ready}
      height="h-[260px]"
    >
      <BarChart
        legend={false}
        labels={SLICE_KEYS.map((key) => SLICE_LABEL[key])}
        datasets={[
          {
            label: "n",
            data: SLICE_KEYS.map((key) => slices[key]),
            color: SLICE_KEYS.map((key) => SLICE_COLOR[key]),
          },
        ]}
      />
    </PaneChart>
  );
}

function TopCellsTable({ cells }: { cells: TopCell[] }) {
  if (cells.length === 0) {
    return <EmptyState title="No trunk × country" body="No call records to group yet." />;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
          <tr>
            <th className="py-2 pr-3">Trunk</th>
            <th className="py-2 pr-3">ISO</th>
            <th className="py-2 pr-3">n</th>
            <th className="py-2 pr-3">Human</th>
            <th className="py-2 pr-3">Screen</th>
            <th className="py-2 pr-3">Machine</th>
            <th className="py-2 pr-3">SIP</th>
            <th className="py-2 pr-3">No media</th>
            <th className="py-2 pr-3">Dest</th>
          </tr>
        </thead>
        <tbody>
          {cells.map((cell) => {
            const voice = voiceOf(cell.voice_mix ?? EMPTY_VOICE);
            const slices = slicesOf(cell.hangup_slices ?? EMPTY_SLICES);
            return (
              <tr key={`${cell.trunk_id}:${cell.country_iso2}`} className="border-t border-white/5">
                <td className="py-2 pr-3 font-mono text-xs">{cell.trunk_id}</td>
                <td className="py-2 pr-3 font-mono">{cell.country_iso2}</td>
                <td className="py-2 pr-3 tabular-nums">{cell.attempts}</td>
                <td className="py-2 pr-3 tabular-nums">{voice.human}</td>
                <td className="py-2 pr-3 tabular-nums">{voice.screening}</td>
                <td className="py-2 pr-3 tabular-nums">{voice.machine}</td>
                <td className="py-2 pr-3 tabular-nums">{slices.sip}</td>
                <td className="py-2 pr-3 tabular-nums">{slices.no_media}</td>
                <td className="py-2 pr-3 tabular-nums">{slices.dest}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OverviewPane({ data, mode, onMode }: { data: Overview; mode: AsrMode; onMode: (next: AsrMode) => void }) {
  const nMin = data.n_min ?? 30;
  const last15 = data.window?.last_15m;
  const yesterday = data.window?.yesterday_same_hour ?? null;
  const voice = voiceOf(data.voice_mix ?? EMPTY_VOICE);
  const slices = slicesOf(data.hangup_slices ?? EMPTY_SLICES);
  const funnel = data.otp_funnel;
  const outcomeRows = chartMix(data.outcome_mix, 8).filter(([key]) => key !== "unset" || mixSum(chartMix(data.outcome_mix)) > 0);
  const hours = data.day_part?.hours ?? [];
  const spark = hours.map((row) => Number(row.human) || 0);
  const top = data.top_cells ?? [];
  return (
    <div className="space-y-6">
      <LowConfidence sampleThin={data.sample_thin} n={data.attempts} nMin={nMin} />
      <p className="text-sm text-mute">
        Counts from call records. Human is not screening and not voicemail. Numbers are never shown.
        Shadow ranks do not pick the Dial trunk.
      </p>
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Attempts" value={num(data.attempts)} hint="call records" />
        <StatCard label="Human" value={num(voice.human)} hint="not inside network ASR" />
        <StatCard label="Screening" value={num(voice.screening)} hint="connecting / IVR" />
        <StatCard label="Machine" value={num(voice.machine)} hint="voicemail / fax" />
        <StatCard label="OTP no digits" value={num(funnel?.no_digits)} hint="prompted, nothing collected" />
        <StatCard label="SIP fails" value={num(slices.sip)} hint="trunk / 5xx / timeout" />
        <StatCard label="No media" value={num(slices.no_media)} hint="dead air / silence" />
        <StatCard label="Dest fails" value={num(slices.dest)} hint="busy / no answer / reject" />
      </div>
      {spark.some((value) => value > 0) ? (
        <Card className="px-5 py-4">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
            Human by hour · {data.day_part?.tz || "UTC"}
          </div>
          <p className="mb-3 text-xs text-mute">
            Clock: {data.day_part?.clock || "timestamp"}. Open Day part for screening and machine.
          </p>
          <Sparkline values={spark} width={560} height={48} className="text-ink" />
        </Card>
      ) : null}
      <div className="grid gap-4 xl:grid-cols-3">
        <VoiceMixChart mix={voice} title="Human / screening / machine" />
        <SliceChart slices={slices} title="Hangup slices" />
        <PaneChart
          title="Judgment mix"
          ariaLabel="Judgment outcome mix"
          emptyTitle="No judgments"
          emptyBody="no judgment_outcome on these records"
          ready={mixSum(outcomeRows.filter(([key]) => key !== "unset")) > 0}
          height="h-[260px]"
        >
          <BarChart
            horizontal
            legend={false}
            labels={outcomeRows.filter(([key]) => key !== "unset").map(([key]) => key)}
            datasets={[
              {
                label: "n",
                data: outcomeRows.filter(([key]) => key !== "unset").map(([, value]) => value),
                color: outcomeRows
                  .filter(([key]) => key !== "unset")
                  .map(([key], index) => sliceColor(key, index)),
              },
            ]}
          />
        </PaneChart>
      </div>
      <Card className="px-5 py-4">
        <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
          Trunk × country
        </div>
        <TopCellsTable cells={top} />
      </Card>
      <AsrToggle value={mode} onChange={onMode} />
      <p className="text-sm text-mute">
        Human ASR is HUMAN only. Network ASR is connect-class. Voicemail is never hidden inside one number.
      </p>
      <MeasuresGrid row={data} mode={mode} />
      {last15 ? <MeasuresGrid title="Window · last 15m" row={last15} mode={mode} /> : null}
      {yesterday ? <MeasuresGrid title="Window · yesterday same hour" row={yesterday} mode={mode} /> : null}
    </div>
  );
}

function MatrixPane({ data, mode }: { data: MatrixPayload; mode: AsrMode }) {
  const cells = data.cells ?? [];
  const plotted = [...cells].sort((a, b) => (b.attempts || 0) - (a.attempts || 0)).slice(0, 12);
  const labels = plotted.map((cell) => `${trunkChartLabel(cell.trunk_id)} · ${cell.country_iso2 || "—"}`);
  return (
    <div className="space-y-4">
      <LowConfidence sampleThin={data.sample_thin} n={data.n} nMin={data.n_min} />
      <p className="text-sm text-mute">
        Human, screening, and machine by trunk and country. Does not pick the Dial trunk.
      </p>
      {plotted.length > 0 ? (
        <Card className="px-5 py-4">
          <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
            Voice mix · top cells
          </div>
          <ChartFrame ariaLabel="Human, screening, and machine by trunk and country" className="h-[280px]">
            <BarChart
              horizontal
              labels={labels}
              datasets={[
                {
                  label: "Human",
                  data: plotted.map((cell) => voiceOf(cell.voice_mix).human),
                  color: VOICE_COLOR.human,
                },
                {
                  label: "Screening",
                  data: plotted.map((cell) => voiceOf(cell.voice_mix).screening),
                  color: VOICE_COLOR.screening,
                },
                {
                  label: "Machine",
                  data: plotted.map((cell) => voiceOf(cell.voice_mix).machine),
                  color: VOICE_COLOR.machine,
                },
              ]}
            />
          </ChartFrame>
        </Card>
      ) : null}
      <Card className="px-5 py-4">
        <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
          Trunk × country
        </div>
        {cells.length === 0 ? (
          <EmptyState title="No cells" body="No call records to group yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
                <tr>
                  <th className="py-2 pr-3">Trunk</th>
                  <th className="py-2 pr-3">ISO</th>
                  <th className="py-2 pr-3">n</th>
                  <th className="py-2 pr-3">Human</th>
                  <th className="py-2 pr-3">Screen</th>
                  <th className="py-2 pr-3">Machine</th>
                  <th className="py-2 pr-3">{mode === "human" ? "Human ASR" : "Network ASR"}</th>
                  <th className="py-2 pr-3">SIP</th>
                  <th className="py-2 pr-3">No media</th>
                  <th className="py-2 pr-3">Dest</th>
                  <th className="py-2 pr-3">Conf</th>
                </tr>
              </thead>
              <tbody>
                {cells.map((cell: MatrixCell) => {
                  const thin = isThin(cell.sample_thin, cell.attempts, cell.n_min ?? data.n_min);
                  const voice = voiceOf(cell.voice_mix);
                  const slices = slicesOf(cell.hangup_slices);
                  return (
                    <tr key={`${cell.trunk_id}:${cell.country_iso2}:${cell.prefix_group ?? ""}`} className="border-t border-white/5">
                      <td className="py-2 pr-3 font-mono text-xs">{cell.trunk_id}</td>
                      <td className="py-2 pr-3 font-mono">{cell.country_iso2}</td>
                      <td className="py-2 pr-3 tabular-nums">{cell.attempts}</td>
                      <td className="py-2 pr-3 tabular-nums">{voice.human}</td>
                      <td className="py-2 pr-3 tabular-nums">{voice.screening}</td>
                      <td className="py-2 pr-3 tabular-nums">{voice.machine}</td>
                      <td className="py-2 pr-3 tabular-nums">{pct(asrOf(cell, mode))}</td>
                      <td className="py-2 pr-3 tabular-nums">{slices.sip}</td>
                      <td className="py-2 pr-3 tabular-nums">{slices.no_media}</td>
                      <td className="py-2 pr-3 tabular-nums">{slices.dest}</td>
                      <td className="py-2 pr-3">
                        {thin ? (
                          <StatusPill tone="live">{`n<${cell.n_min ?? data.n_min ?? 30}`}</StatusPill>
                        ) : (
                          <StatusPill tone="ok">ok</StatusPill>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ShadowScoreChart({ rows }: { rows: BestPathPayload["best"] }) {
  const eligible = rows.filter(
    (row) => row.eligible && row.score != null && Number.isFinite(Number(row.score)),
  );
  if (eligible.length === 0) return null;
  return (
    <ChartFrame
      ariaLabel="Shadow score by trunk and country. Does not pick the Dial trunk."
      className="h-[220px]"
    >
      <BarChart
        horizontal
        legend={false}
        labels={eligible.map((row) => `${trunkChartLabel(row.trunk_id)} · ${row.country_iso2 || "—"}`)}
        datasets={[
          {
            label: "Shadow score",
            data: eligible.map((row) => Number(row.score)),
            color: eligible.map((row) => trunkColor(row.trunk_id)),
          },
        ]}
      />
    </ChartFrame>
  );
}

function BestPathPane({ data }: { data: BestPathPayload }) {
  const nMin = data.n_min ?? 30;
  const ranked = data.ranked ?? [];
  const best = data.best ?? [];
  const thin =
    Boolean(data.sample_thin) ||
    ranked.length === 0 ||
    ranked.every((row) => !row.eligible || row.n < nMin);
  return (
    <div className="space-y-4">
      <ShadowBanner extra={data.label} />
      <LowConfidence sampleThin={thin} nMin={nMin} />
      <Card className="px-5 py-4">
        <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
          Shadow best by country
        </div>
        {thin || best.length === 0 ? (
          <EmptyState
            title={thin ? `low confidence (n<${nMin})` : "No ranks"}
            body="Need enough attempts (n_min) before a shadow path is treated as proven."
          />
        ) : (
          <div className="space-y-4">
            <ShadowScoreChart rows={best} />
            <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
                <tr>
                  <th className="py-2 pr-3">ISO</th>
                  <th className="py-2 pr-3">Trunk</th>
                  <th className="py-2 pr-3">Score</th>
                  <th className="py-2 pr-3">n</th>
                  <th className="py-2 pr-3">Eligible</th>
                  <th className="py-2 pr-3">Reasons</th>
                </tr>
              </thead>
              <tbody>
                {best.map((row) => (
                  <tr key={`${row.country_iso2}:${row.trunk_id}`} className="border-t border-white/5">
                    <td className="py-2 pr-3 font-mono">{row.country_iso2}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{row.trunk_id}</td>
                    <td className="py-2 pr-3 tabular-nums">
                      {row.score == null ? "—" : Number(row.score).toFixed(3)}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{row.n}</td>
                    <td className="py-2 pr-3">
                      <StatusPill tone={row.eligible ? "ok" : "live"}>
                        {row.eligible ? "eligible" : `n<${nMin}`}
                      </StatusPill>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-mute">
                      {(row.reasons ?? []).filter((item) => !isForbiddenKey(item)).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </Card>
      <Card className="px-5 py-4">
        <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Ranked</div>
        {ranked.length === 0 ? (
          <EmptyState title="No ranked rows" body="Score job has not emitted ranked_trunks yet." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
                <tr>
                  <th className="py-2 pr-3">Trunk</th>
                  <th className="py-2 pr-3">Prefix</th>
                  <th className="py-2 pr-3">ISO</th>
                  <th className="py-2 pr-3">Score</th>
                  <th className="py-2 pr-3">n</th>
                  <th className="py-2 pr-3">Reasons</th>
                </tr>
              </thead>
              <tbody>
                {ranked.map((row, index) => (
                  <tr key={`${row.trunk_id}:${row.prefix_group ?? ""}:${index}`} className="border-t border-white/5">
                    <td className="py-2 pr-3 font-mono text-xs">{row.trunk_id}</td>
                    <td className="py-2 pr-3 text-mute">{row.prefix_group ?? "—"}</td>
                    <td className="py-2 pr-3 font-mono">{row.country_iso2 ?? "—"}</td>
                    <td className="py-2 pr-3 tabular-nums">
                      {row.score == null ? "—" : Number(row.score).toFixed(3)}
                    </td>
                    <td className="py-2 pr-3 tabular-nums">{row.n}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-mute">
                      {(row.reasons ?? []).filter((item) => !isForbiddenKey(item)).join(" · ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function FailuresPane({ data }: { data: FailuresPayload }) {
  const slices = slicesOf(data.hangup_slices ?? EMPTY_SLICES);
  const causes = chartMix(data.hangup_cause, 8);
  const reasons = chartMix(data.reason_codes, 8);
  return (
    <div className="space-y-4">
      <LowConfidence sampleThin={data.sample_thin} n={data.n} nMin={data.n_min} />
      <p className="text-sm text-mute">
        SIP is a trunk or network cause. No media is dead air after the attempt. Dest is busy, no
        answer, or reject. Clearing, voicemail drop, and operator hangup stay in Other.
      </p>
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="SIP" value={num(slices.sip)} />
        <StatCard label="No media" value={num(slices.no_media)} />
        <StatCard label="Dest" value={num(slices.dest)} />
        <StatCard label="Live routing" value={data.live_routing ? "on" : "off"} hint="always off" />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <SliceChart slices={slices} title="SIP vs no-media vs dest" />
        <PaneChart
          title="Hangup cause"
          ariaLabel="Top hangup causes"
          emptyTitle="No hangup cause"
          emptyBody="no hangup_cause on these records"
          ready={mixSum(causes) > 0}
          height="h-[260px]"
        >
          <BarChart
            horizontal
            legend={false}
            labels={causes.map(([key]) => key)}
            datasets={[
              {
                label: "n",
                data: causes.map(([, value]) => value),
                color: causes.map(([key], index) => sliceColor(key, index)),
              },
            ]}
          />
        </PaneChart>
      </div>
      <MixTable title="Hangup cause" mix={data.hangup_cause} empty="no hangup_cause yet" />
      <AnatomyTable anatomy={data.failure_anatomy ?? EMPTY_ANATOMY} />
      <MixTable title="Success class" mix={data.success_class} empty="no success_class yet" />
      <MixTable title="Reason codes" mix={reasons.length ? data.reason_codes : data.reason_codes} empty="no reason codes yet" />
    </div>
  );
}

function ClassificationPane({ data }: { data: ClassificationPayload }) {
  const mix = chartMix(data.classification_mix);
  const causes = chartMix(data.hangup_cause, 8);
  return (
    <div className="space-y-4">
      <LowConfidence sampleThin={data.sample_thin} n={data.n} nMin={data.n_min} />
      <div className="grid gap-4 xl:grid-cols-2">
        <PaneChart
          title="Classification mix"
          ariaLabel="Far-end classification mix"
          emptyTitle="No classification mix"
          emptyBody="no far_end_class yet"
          ready={mixSum(mix) > 0}
        >
          <DoughnutChart
            slices={mix.map(([key, value], index) => ({
              label: key,
              value,
              color: sliceColor(key, index),
            }))}
          />
        </PaneChart>
        <PaneChart
          title="Hangup cause"
          ariaLabel="Top hangup causes"
          emptyTitle="No hangup cause"
          emptyBody="no hangup_cause yet"
          ready={mixSum(causes) > 0}
          height="h-[260px]"
        >
          <BarChart
            horizontal
            legend={false}
            labels={causes.map(([key]) => key)}
            datasets={[
              {
                label: "n",
                data: causes.map(([, value]) => value),
                color: causes.map(([key], index) => sliceColor(key, index)),
              },
            ]}
          />
        </PaneChart>
      </div>
      <MixTable title="Classification mix" mix={data.classification_mix} empty="no far_end_class yet" />
      <MixTable title="Hangup cause" mix={data.hangup_cause} empty="no hangup_cause yet" />
    </div>
  );
}

function AmdPane({ data }: { data: AmdQualityPayload }) {
  const amdRows = chartMix(data.amd_mix);
  const quality: Array<{ label: string; value: number; color: string }> = [
    { label: "Human", value: Number(data.human) || 0, color: AMD_COLOR.HUMAN },
    { label: "Voicemail", value: Number(data.voicemail) || 0, color: AMD_COLOR.MACHINE },
    { label: "Connecting leftover", value: Number(data.connecting_leftover) || 0, color: "#d8a25c" },
    { label: "Empty RX", value: Number(data.empty_rx) || 0, color: AMD_COLOR.UNKNOWN },
  ].filter((row) => row.value > 0);
  return (
    <div className="space-y-4">
      <LowConfidence sampleThin={data.sample_thin} n={data.n} nMin={data.n_min} />
      <p className="text-sm text-mute">
        Connecting TTS is never HUMAN. MACHINE maps to voicemail. Leftover connecting RX is counted separately.
      </p>
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Human" value={num(data.human)} hint="far_end HUMAN" />
        <StatCard label="Voicemail" value={num(data.voicemail)} hint="not inside human ASR" />
        <StatCard label="Connecting leftover" value={num(data.connecting_leftover)} />
        <StatCard label="Empty RX" value={num(data.empty_rx)} />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <PaneChart
          title="AMD mix"
          ariaLabel="AMD mix doughnut"
          emptyTitle="No AMD mix"
          emptyBody="no AMD labels yet"
          ready={mixSum(amdRows) > 0}
        >
          <DoughnutChart
            slices={amdRows.map(([key, value], index) => ({
              label: key,
              value,
              color: sliceColor(key, index),
            }))}
          />
        </PaneChart>
        <PaneChart
          title="RX quality"
          hint="Connecting leftover is not human."
          ariaLabel="Human, voicemail, connecting leftover, and empty RX"
          emptyTitle="No RX quality"
          emptyBody="no far-end quality counts yet"
          ready={quality.length > 0}
        >
          <BarChart
            legend={false}
            labels={quality.map((row) => row.label)}
            datasets={[
              {
                label: "n",
                data: quality.map((row) => row.value),
                color: quality.map((row) => row.color),
              },
            ]}
          />
        </PaneChart>
      </div>
      <MixTable title="AMD mix" mix={data.amd_mix} empty="no AMD labels yet" />
      <Card className="px-5 py-4">
        <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Notes</div>
        {data.notes?.length ? (
          <ul className="list-disc space-y-1 pl-5 text-sm text-mute">
            {data.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No notes" body="AMD quality notes will land with the stats API." />
        )}
      </Card>
    </div>
  );
}

function TrunksPane({ rows, cells }: { rows: TrunkHealth[]; cells?: MatrixCell[] }) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No trunk health"
        body="Sofia health has not published yet. Empty is valid — no secrets are shown."
      />
    );
  }
  const stateCounts = SOFIA_STATES.map((state) => ({
    state,
    n: rows.filter((row) => sofiaStateBucket(row.state) === state).length,
  })).filter((row) => row.n > 0);
  const attempts = trunkAttemptBars(cells);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 xl:grid-cols-2">
        <PaneChart
          title="Sofia state"
          ariaLabel="SIP trunk sofia state counts"
          emptyTitle="No sofia state"
          emptyBody="No registration state yet."
          ready={stateCounts.length > 0}
        >
          <BarChart
            legend={false}
            labels={stateCounts.map((row) => row.state)}
            datasets={[
              {
                label: "Trunks",
                data: stateCounts.map((row) => row.n),
                color: stateCounts.map((row) => STATE_COLOR[row.state]),
              },
            ]}
          />
        </PaneChart>
        <PaneChart
          title="Attempts by trunk"
          hint="Historical CDR counts. Does not pick the Dial trunk."
          ariaLabel="Attempts by SIP trunk. Does not pick the Dial trunk."
          emptyTitle="No trunk attempts"
          emptyBody="Matrix has not grouped attempts yet."
          ready={attempts.length > 0}
          height="h-[240px]"
        >
          <BarChart
            horizontal
            legend={false}
            labels={attempts.map((row) => trunkChartLabel(row.id))}
            datasets={[
              {
                label: "Attempts",
                data: attempts.map((row) => row.n),
                color: attempts.map((row) => trunkColor(row.id)),
              },
            ]}
          />
        </PaneChart>
      </div>
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Trunk health</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
            <tr>
              <th className="py-2 pr-3">Id</th>
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">State</th>
              <th className="py-2 pr-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-white/5">
                <td className="py-2 pr-3 font-mono text-xs">{row.id}</td>
                <td className="py-2 pr-3">{row.display ? row.display : "—"}</td>
                <td className="py-2 pr-3">
                  <StatusPill tone={statusTone(row.state)}>{row.state ?? "—"}</StatusPill>
                </td>
                <td className="py-2 pr-3">
                  <StatusPill tone={statusTone(row.status)}>{row.status ?? "—"}</StatusPill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
    </div>
  );
}

function CatalogPane({ rows }: { rows: CatalogRow[] }) {
  if (rows.length === 0) {
    return <EmptyState title="Empty catalog" body="Catalog steward has not seeded ITU rows." />;
  }
  return (
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Dial catalog</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
            <tr>
              <th className="py-2 pr-3">ISO</th>
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 pr-3">CC</th>
              <th className="py-2 pr-3">NSN</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.iso2} className="border-t border-white/5">
                <td className="py-2 pr-3 font-mono">{row.iso2}</td>
                <td className="py-2 pr-3">{row.name}</td>
                <td className="py-2 pr-3">+{row.calling_code}</td>
                <td className="py-2 pr-3 text-mute">
                  {row.nsn_min}–{row.nsn_max}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * Judgment chip tone. NEVER "fail": the CDR judgment scores expected-code
 * match only — an operator-Accepted CONNECT_HUMAN success can legitimately
 * carry `code-match: fail` (FC8) and must not be painted red.
 */
function codeMatchTone(outcome: string | null | undefined): "ok" | "mute" {
  return (outcome ?? "").trim().toLowerCase() === "success" ? "ok" : "mute";
}

function JudgmentsPane({ data }: { data: JudgmentsPayload }) {
  const rows = data.judgments ?? [];
  const mixRows = chartMix(data.outcome_mix).filter(([key]) => key !== "unset");
  return (
    <div className="space-y-4">
      <p className="text-sm text-mute">
        Outcome mix is from call records. Unset means the judge has not written a row yet. This is
        not a trunk recommendation.
      </p>
      <div className="grid gap-3 md:grid-cols-3">
        {(["success", "fail", "void"] as const).map((key) => (
          <StatCard key={key} label={key} value={num(data.outcome_mix?.[key] ?? 0)} />
        ))}
      </div>
      <PaneChart
        title="Outcome mix"
        ariaLabel="Judgment outcome mix from call records"
        emptyTitle="No outcome mix"
        emptyBody="no judgment_outcome on these records"
        ready={mixSum(mixRows) > 0}
      >
        <DoughnutChart
          slices={mixRows.map(([key, value], index) => ({
            label: key,
            value,
            color: sliceColor(key, index),
          }))}
        />
      </PaneChart>
      {rows.length === 0 ? (
        <EmptyState title="No judgment rows" body="The judgments table is empty. The mix above still counts CDR outcomes." />
      ) : (
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
        Recent judgments
      </div>
      <p className="mb-3 text-sm text-mute">
        Expected-code match judgment from the CDR judge — not the call outcome. A human pickup
        that the operator Accepted can still show <span className="font-mono">code-match: fail</span>;
        check History Outcome for what actually happened on the call.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
            <tr>
              <th className="py-2 pr-3">Call</th>
              <th className="py-2 pr-3" title="Expected-code match judgment — not the call outcome">
                Code-match
              </th>
              <th className="py-2 pr-3">Reason</th>
              <th className="py-2 pr-3">Far end</th>
              <th className="py-2 pr-3">Success</th>
              <th className="py-2 pr-3">Trunk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.call_id}:${index}`} className="border-t border-white/5">
                <td className="py-2 pr-3 font-mono text-xs">{row.call_id}</td>
                <td className="py-2 pr-3">
                  <StatusPill tone={codeMatchTone(row.outcome)}>{`code-match: ${row.outcome}`}</StatusPill>
                </td>
                <td className="py-2 pr-3 font-mono text-xs">{row.outcome_reason}</td>
                <td className="py-2 pr-3 font-mono text-xs text-mute">{row.far_end_class}</td>
                <td className="py-2 pr-3 font-mono text-xs text-mute">{row.success_class}</td>
                <td className="py-2 pr-3 font-mono text-xs">{row.trunk_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
      )}
    </div>
  );
}

function OtpPane({ data }: { data: OtpFunnelPayload }) {
  const mix = data.otp ?? {};
  const rows = chartMix(mix);
  const funnel: OtpFunnel = {
    attempts: Number(data.funnel?.attempts ?? data.n ?? 0) || 0,
    human: Number(data.funnel?.human ?? 0) || 0,
    prompted: Number(data.funnel?.prompted ?? 0) || 0,
    no_digits: Number(data.funnel?.no_digits ?? data.no_digits ?? 0) || 0,
    collected: Number(data.funnel?.collected ?? 0) || 0,
    verified: Number(data.funnel?.verified ?? 0) || 0,
    mismatch: Number(data.funnel?.mismatch ?? 0) || 0,
    not_reached: Number(data.funnel?.not_reached ?? 0) || 0,
  };
  const stages: Array<[string, number]> = [
    ["Prompted", funnel.prompted],
    ["No digits", funnel.no_digits],
    ["Collected", funnel.collected],
    ["Verified", funnel.verified],
    ["Mismatch", funnel.mismatch],
  ];
  return (
    <div className="space-y-4">
      <p className="text-sm text-mute">
        First-party funnel from call records. Digits are never shown. No digits means the prompt
        ran and nothing was collected. Operator Accept in History stays the call outcome.
      </p>
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Attempts" value={num(funnel.attempts)} />
        <StatCard label="Human" value={num(funnel.human)} />
        <StatCard label="No digits" value={num(funnel.no_digits)} hint="prompted, empty collect" />
        <StatCard label="Verified" value={num(funnel.verified)} />
      </div>
      <PaneChart
        title="OTP funnel"
        ariaLabel="OTP funnel counts. Digits are not shown."
        emptyTitle="No OTP funnel"
        emptyBody="no prompted or collected calls yet"
        ready={stages.some(([, value]) => value > 0)}
        height="h-[220px]"
      >
        <BarChart
          legend={false}
          labels={stages.map(([key]) => key)}
          datasets={[
            {
              label: "n",
              data: stages.map(([, value]) => value),
              color: stages.map(([key], index) => (key === "No digits" ? "#e57584" : sliceColor(key, index))),
            },
          ]}
        />
      </PaneChart>
      <PaneChart
        title="OTP state"
        ariaLabel="First-party OTP state counts. Digits are not shown."
        emptyTitle="No OTP events"
        emptyBody="no first-party OTP events yet"
        ready={mixSum(rows) > 0}
        height="h-[220px]"
      >
        <BarChart
          legend={false}
          labels={rows.map(([key]) => key)}
          datasets={[
            {
              label: "n",
              data: rows.map(([, value]) => value),
              color: rows.map(([key], index) => sliceColor(key, index)),
            },
          ]}
        />
      </PaneChart>
      <MixTable title="OTP state" mix={mix} empty="no first-party OTP events yet" />
    </div>
  );
}

function DayPartPane({ data }: { data: DayPartPayload }) {
  const hours = data.hours ?? [];
  const ready = hours.some((row) => (Number(row.attempts) || 0) > 0);
  return (
    <div className="space-y-4">
      <LowConfidence sampleThin={data.sample_thin} n={data.n} nMin={data.n_min} />
      <p className="text-sm text-mute">
        Hour of day in {data.tz || "UTC"}. Clock source: {data.clock || "none"}. created_at wins
        when the record has it. Does not pick a trunk.
      </p>
      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Attempts" value={num(data.n)} />
        <StatCard label="Human" value={num(data.voice_mix?.human)} />
        <StatCard label="Screening" value={num(data.voice_mix?.screening)} />
        <StatCard label="Machine" value={num(data.voice_mix?.machine)} />
      </div>
      <PaneChart
        title="Voice by hour"
        ariaLabel="Human, screening, and machine by UTC hour"
        emptyTitle="No day part"
        emptyBody="no created_at or timestamp on these records"
        ready={ready}
        height="h-[280px]"
      >
        <BarChart
          labels={hours.map((row) => row.label || String(row.hour).padStart(2, "0"))}
          datasets={[
            {
              label: "Human",
              data: hours.map((row) => Number(row.human) || 0),
              color: VOICE_COLOR.human,
            },
            {
              label: "Screening",
              data: hours.map((row) => Number(row.screening) || 0),
              color: VOICE_COLOR.screening,
            },
            {
              label: "Machine",
              data: hours.map((row) => Number(row.machine) || 0),
              color: VOICE_COLOR.machine,
            },
          ]}
        />
      </PaneChart>
    </div>
  );
}

function AgentsPane({ rows }: { rows: AgentRow[] }) {
  if (rows.length === 0) {
    return <EmptyState title="No agents" body="Registry empty until the web process seeds sqlite." />;
  }
  return (
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Agent roster</div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-[0.14em] text-mute">
            <tr>
              <th className="py-2 pr-3">Agent</th>
              <th className="py-2 pr-3">Class</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Owns</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.agent_id} className="border-t border-white/5">
                <td className="py-2 pr-3 font-mono text-xs">{row.agent_id}</td>
                <td className="py-2 pr-3">{row.class}</td>
                <td className="py-2 pr-3">
                  <StatusPill tone={statusTone(row.status)}>{row.status}</StatusPill>
                </td>
                <td className="py-2 pr-3 text-mute">{row.owns}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PagesPane({ rows }: { rows: PageRow[] }) {
  if (rows.length === 0) {
    return <EmptyState title="No pages" body="Composer has not registered seed pages yet." />;
  }
  return (
    <Card className="px-5 py-4">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">Pages</div>
      <ul className="space-y-2 text-sm">
        {rows.map((page) => (
          <li key={page.page_id} className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-xs text-teal">{page.page_id}</span>
            <span className="text-ink">{page.job}</span>
            <StatusPill tone={statusTone(page.status)}>{page.status}</StatusPill>
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function VoiceOps() {
  const [tab, setTab] = useState<TabId>("overview");
  const [asrMode, setAsrMode] = useState<AsrMode>("human");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [matrix, setMatrix] = useState<MatrixPayload | null>(null);
  const [bestPath, setBestPath] = useState<BestPathPayload | null>(null);
  const [failures, setFailures] = useState<FailuresPayload | null>(null);
  const [classification, setClassification] = useState<ClassificationPayload | null>(null);
  const [amd, setAmd] = useState<AmdQualityPayload | null>(null);
  const [trunks, setTrunks] = useState<TrunkHealth[] | null>(null);
  const [catalog, setCatalog] = useState<CatalogRow[] | null>(null);
  const [judgments, setJudgments] = useState<JudgmentsPayload | null>(null);
  const [otp, setOtp] = useState<OtpFunnelPayload | null>(null);
  const [dayPart, setDayPart] = useState<DayPartPayload | null>(null);
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [pages, setPages] = useState<PageRow[] | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;
    setError(null);
    setLoading(true);
    (async () => {
      try {
        switch (tab) {
          case "overview": {
            const body = await request<Overview>("/api/orchestrator/overview", { signal: ac.signal });
            if (!cancelled) setOverview(body);
            break;
          }
          case "matrix": {
            const body = await request<MatrixPayload>("/api/orchestrator/stats/matrix", {
              signal: ac.signal,
            });
            if (!cancelled) setMatrix(body);
            break;
          }
          case "best-path": {
            const body = await request<BestPathPayload>("/api/orchestrator/stats/best-path", {
              signal: ac.signal,
            });
            if (!cancelled) setBestPath(body);
            break;
          }
          case "failures": {
            const body = await request<FailuresPayload>("/api/orchestrator/stats/failures", {
              signal: ac.signal,
            });
            if (!cancelled) setFailures(body);
            break;
          }
          case "classification": {
            const body = await request<ClassificationPayload>("/api/orchestrator/stats/classification", {
              signal: ac.signal,
            });
            if (!cancelled) setClassification(body);
            break;
          }
          case "amd-quality": {
            const body = await request<AmdQualityPayload>("/api/orchestrator/stats/amd-quality", {
              signal: ac.signal,
            });
            if (!cancelled) setAmd(body);
            break;
          }
          case "trunks": {
            const [health, matrixBody] = await Promise.all([
              requestOptional<unknown>("/api/orchestrator/health/trunks", ac.signal),
              requestOptional<MatrixPayload>("/api/orchestrator/stats/matrix", ac.signal),
            ]);
            if (!cancelled) {
              setTrunks(parseTrunks(health));
              if (matrixBody && typeof matrixBody === "object") setMatrix(matrixBody);
            }
            break;
          }
          case "catalog": {
            const body = await request<CatalogPayload>("/api/orchestrator/catalog", { signal: ac.signal });
            if (!cancelled) setCatalog(body.catalog ?? []);
            break;
          }
          case "judgments": {
            const body = await request<JudgmentsPayload>("/api/orchestrator/judgments", {
              signal: ac.signal,
            });
            if (!cancelled) setJudgments(body);
            break;
          }
          case "otp": {
            const body = await request<OtpFunnelPayload>("/api/orchestrator/otp-funnel", {
              signal: ac.signal,
            });
            if (!cancelled) setOtp(body);
            break;
          }
          case "day-part": {
            const body = await request<DayPartPayload>("/api/orchestrator/stats/day-part", {
              signal: ac.signal,
            });
            if (!cancelled) setDayPart(body);
            break;
          }
          case "agents": {
            const body = await request<AgentsPayload>("/api/orchestrator/agents", { signal: ac.signal });
            if (!cancelled) setAgents(body.agents ?? []);
            break;
          }
          case "pages": {
            const body = await request<PagesPayload>("/api/orchestrator/pages", { signal: ac.signal });
            if (!cancelled) setPages(body.pages ?? []);
            break;
          }
          default:
            break;
        }
      } catch (err) {
        if (cancelled || ac.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Could not load voice ops");
      } finally {
        if (!cancelled && !ac.signal.aborted) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [tab]);

  let pane: ReactNode = null;
  if (tab === "overview" && overview) pane = <OverviewPane data={overview} mode={asrMode} onMode={setAsrMode} />;
  if (tab === "matrix" && matrix) pane = <MatrixPane data={matrix} mode={asrMode} />;
  if (tab === "best-path" && bestPath) pane = <BestPathPane data={bestPath} />;
  if (tab === "failures" && failures) pane = <FailuresPane data={failures} />;
  if (tab === "classification" && classification) pane = <ClassificationPane data={classification} />;
  if (tab === "amd-quality" && amd) pane = <AmdPane data={amd} />;
  if (tab === "trunks" && trunks) pane = <TrunksPane rows={trunks} cells={matrix?.cells} />;
  if (tab === "catalog" && catalog) pane = <CatalogPane rows={catalog} />;
  if (tab === "judgments" && judgments) pane = <JudgmentsPane data={judgments} />;
  if (tab === "otp" && otp) pane = <OtpPane data={otp} />;
  if (tab === "day-part" && dayPart) pane = <DayPartPane data={dayPart} />;
  if (tab === "agents" && agents) pane = <AgentsPane rows={agents} />;
  if (tab === "pages" && pages) pane = <PagesPane rows={pages} />;

  return (
    <>
      <PageHeader
        kicker="orchestrator"
        title="Voice ops"
        actions={
          <Link to="/stats" className="text-sm text-ink-2 hover:text-ink">
            Call stats
          </Link>
        }
      />
      <PageBody>
        <TabNav tab={tab} onChange={setTab} />
        {error ? <EmptyState title="Unavailable" body={error} /> : null}
        {!error && loading && !pane ? <p className="text-sm text-mute">Loading…</p> : null}
        {!error ? pane : null}
      </PageBody>
    </>
  );
}
